import { useCallback, useEffect, useState } from 'react';
import {
  getBillingMilestones,
  createBillingMilestone,
  seedBillingPreset,
  updateBillingMilestone,
  deleteBillingMilestone,
  generateDrawInvoice,
  type BillingMilestone,
  type BillingContract,
} from '@/lib/api/billing';
import { useToast } from '@/lib/hooks/useToast';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';

interface DrawBillingPanelProps {
  jobId: string;
  onInvoiceCreated: () => void;
}

const money = (n: number) =>
  `$${(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Milestone status: pending (no invoice) → invoiced (draft/sent) → paid.
const statusChip = (m: BillingMilestone) => {
  if (!m.invoice_id) return { label: 'Pending', cls: 'bg-gray-100 text-gray-500' };
  if (m.invoice_status === 'paid') return { label: 'Paid', cls: 'bg-success/10 text-success' };
  if (m.invoice_status === 'overdue') return { label: 'Overdue', cls: 'bg-critical/10 text-critical' };
  return { label: `Invoiced (${m.invoice_status})`, cls: 'bg-warning/10 text-warning' };
};

// Draw billing schedule (CR #78): residential milestone billing — deposit →
// rough-in → completion — with one-click draft draw invoices per milestone.
export const DrawBillingPanel = ({ jobId, onInvoiceCreated }: DrawBillingPanelProps) => {
  const { toast } = useToast();
  const [milestones, setMilestones] = useState<BillingMilestone[]>([]);
  const [contract, setContract] = useState<BillingContract | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<BillingMilestone | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);

  // add/edit form state
  const [fTitle, setFTitle] = useState('');
  const [fType, setFType] = useState<'percent' | 'fixed'>('percent');
  const [fValue, setFValue] = useState('');

  const load = useCallback(async () => {
    try {
      const d = await getBillingMilestones(jobId);
      setMilestones(d.milestones);
      setContract(d.contract);
    } catch { /* section header still renders */ }
    finally { setLoading(false); }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  const drawAmount = (m: BillingMilestone) =>
    m.amount_type === 'percent' ? ((contract?.revised || 0) * Number(m.amount_value)) / 100 : Number(m.amount_value);

  const pctAllocated = milestones
    .filter(m => m.amount_type === 'percent')
    .reduce((s, m) => s + Number(m.amount_value), 0);

  const startAdd = () => { setEditingId(null); setFTitle(''); setFType('percent'); setFValue(''); setShowAdd(true); };
  const startEdit = (m: BillingMilestone) => {
    setEditingId(m.id); setFTitle(m.title); setFType(m.amount_type); setFValue(String(Number(m.amount_value))); setShowAdd(true);
  };

  const saveForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fTitle.trim() || !(parseFloat(fValue) > 0)) { toast.error('Enter a title and a positive amount'); return; }
    try {
      if (editingId) {
        await updateBillingMilestone(jobId, editingId, { title: fTitle.trim(), amount_type: fType, amount_value: parseFloat(fValue) });
        toast.success('Milestone updated');
      } else {
        await createBillingMilestone(jobId, { title: fTitle.trim(), amount_type: fType, amount_value: parseFloat(fValue) });
        toast.success('Milestone added');
      }
      setShowAdd(false);
      load();
    } catch { toast.error('Failed to save milestone'); }
  };

  const seedPreset = async () => {
    try {
      await seedBillingPreset(jobId);
      toast.success('Standard draw schedule added — adjust to suit the contract');
      load();
    } catch { toast.error('Failed to add schedule'); }
  };

  const generate = async (m: BillingMilestone) => {
    setBusyId(m.id);
    try {
      const r = await generateDrawInvoice(jobId, m.id);
      toast.success(`Draft invoice ${r.invoice.invoiceNumber} created — review & send it below`);
      load();
      onInvoiceCreated();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Failed to generate the draw invoice');
    } finally { setBusyId(null); }
  };

  const doDelete = async (m: BillingMilestone) => {
    setConfirmDelete(null);
    try {
      await deleteBillingMilestone(jobId, m.id);
      setMilestones(prev => prev.filter(x => x.id !== m.id));
    } catch { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="h-16 animate-pulse bg-gray-100 rounded-xl mb-4" />;

  return (
    <div className="mb-5 border border-gray-200 rounded-xl bg-white overflow-hidden">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-gray-50">
        <span className={`material-symbols-outlined text-lg text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`}>chevron_right</span>
        <span className="material-symbols-outlined text-lg text-primary">flag_circle</span>
        <span className="text-sm font-bold text-gray-900 flex-1">Draw billing schedule</span>
        {contract && (
          <span className="text-xs text-gray-500">
            Contract <span className="font-bold text-gray-800">{money(contract.revised)}</span>
            {contract.revised !== contract.budget && <span className="text-gray-400"> (incl. change orders)</span>}
          </span>
        )}
      </button>

      {open && (
        <div className="border-t border-gray-100 px-4 py-3">
          {milestones.length === 0 ? (
            <div className="py-3">
              <p className="text-sm text-gray-500 mb-3">
                Bill this job in draws instead of one lump invoice: define milestones (deposit → rough-in → completion) as
                a % of the contract or fixed amounts, then generate each draw invoice in one click.
              </p>
              <div className="flex flex-wrap gap-2">
                <button onClick={seedPreset}
                  className="h-9 px-3 rounded-lg bg-primary text-white text-sm font-semibold hover:opacity-90">
                  Use standard residential schedule
                </button>
                <button onClick={startAdd}
                  className="h-9 px-3 rounded-lg border border-gray-300 text-sm font-semibold text-gray-600 hover:bg-gray-50">
                  Add milestones manually
                </button>
              </div>
            </div>
          ) : (
            <>
              <ul className="divide-y divide-gray-50">
                {milestones.map((m, i) => {
                  const chip = statusChip(m);
                  return (
                    <li key={m.id} className="py-2 flex flex-wrap items-center gap-2">
                      <span className="text-xs font-bold text-gray-300 w-5 text-right">{i + 1}.</span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">{m.title}</p>
                        <p className="text-[11px] text-gray-400">
                          {m.amount_type === 'percent' ? `${Number(m.amount_value)}% of contract` : 'Fixed amount'}
                          {m.invoice_number && <> · Invoice <span className="font-semibold text-gray-500">{m.invoice_number}</span></>}
                        </p>
                      </div>
                      <span className="text-sm font-bold text-gray-900 whitespace-nowrap">{money(drawAmount(m))}</span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${chip.cls}`}>{chip.label}</span>
                      {!m.invoice_id ? (
                        <>
                          <button onClick={() => generate(m)} disabled={busyId === m.id}
                            className="h-8 px-2.5 rounded-lg bg-primary text-white text-xs font-semibold hover:opacity-90 disabled:opacity-50 whitespace-nowrap">
                            {busyId === m.id ? '…' : 'Generate invoice'}
                          </button>
                          <button onClick={() => startEdit(m)} title="Edit"
                            className="h-8 w-8 rounded-lg text-gray-400 hover:text-primary hover:bg-primary/10 flex items-center justify-center">
                            <span className="material-symbols-outlined text-base">edit</span>
                          </button>
                          <button onClick={() => setConfirmDelete(m)} title="Delete"
                            className="h-8 w-8 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center">
                            <span className="material-symbols-outlined text-base">delete</span>
                          </button>
                        </>
                      ) : (
                        <span className="w-[7.5rem]" />
                      )}
                    </li>
                  );
                })}
              </ul>
              <div className="mt-2 pt-2 border-t border-gray-100 flex flex-wrap items-center gap-3">
                <button onClick={startAdd} className="inline-flex items-center gap-1 text-sm text-primary font-medium hover:underline">
                  <span className="material-symbols-outlined text-base">add</span> Add milestone
                </button>
                {pctAllocated > 0 && (
                  <span className={`text-xs ${Math.abs(pctAllocated - 100) < 0.01 ? 'text-success' : 'text-warning'}`}>
                    {pctAllocated}% of contract allocated{Math.abs(pctAllocated - 100) < 0.01 ? ' ✓' : ` — ${Math.round((100 - pctAllocated) * 100) / 100}% ${pctAllocated < 100 ? 'unallocated' : 'over'}`}
                  </span>
                )}
                {contract && contract.revised === 0 && (
                  <span className="text-xs text-critical">Job has no budget — % draws compute to $0. Set the budget on the job.</span>
                )}
              </div>
            </>
          )}

          {showAdd && (
            <form onSubmit={saveForm} className="mt-3 p-3 rounded-lg bg-gray-50 border border-gray-200 flex flex-wrap items-end gap-2">
              <label className="flex flex-col gap-1 flex-1 min-w-[10rem]">
                <span className="text-[11px] font-medium text-gray-500">Milestone</span>
                <input value={fTitle} onChange={e => setFTitle(e.target.value)} placeholder="e.g. Framing & lock-up"
                  className="h-9 px-2 rounded-lg border border-gray-300 text-sm" autoFocus />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-[11px] font-medium text-gray-500">Type</span>
                <select value={fType} onChange={e => setFType(e.target.value as 'percent' | 'fixed')}
                  className="h-9 px-2 rounded-lg border border-gray-300 text-sm">
                  <option value="percent">% of contract</option>
                  <option value="fixed">Fixed $</option>
                </select>
              </label>
              <label className="flex flex-col gap-1 w-24">
                <span className="text-[11px] font-medium text-gray-500">{fType === 'percent' ? 'Percent' : 'Amount'}</span>
                <input type="number" step="0.01" min="0" value={fValue} onChange={e => setFValue(e.target.value)}
                  placeholder={fType === 'percent' ? '25' : '5000'} className="h-9 px-2 rounded-lg border border-gray-300 text-sm" />
              </label>
              <button type="submit" className="h-9 px-3 rounded-lg bg-primary text-white text-sm font-semibold hover:opacity-90">
                {editingId ? 'Save' : 'Add'}
              </button>
              <button type="button" onClick={() => setShowAdd(false)}
                className="h-9 px-3 rounded-lg border border-gray-300 text-sm font-semibold text-gray-600">Cancel</button>
            </form>
          )}
        </div>
      )}

      {confirmDelete && (
        <ConfirmDialog
          message={`Delete milestone "${confirmDelete.title}"?`}
          onConfirm={() => doDelete(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};
